import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useInsertionEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// Reimplements @radix-ui/react-use-controllable-state so we don't pull
// a Radix package just for one hook (Base UI is the primitive layer).
// Same public API: pass `{ prop, defaultProp, onChange }`, get back
// `[value, setValue]`. `defaultProp` is required so the return type is
// `T` (not `T | undefined`); pass `T | undefined` if your value type
// includes undefined.

type ChangeHandler<T> = (state: T) => void;
type SetStateFn<T> = Dispatch<SetStateAction<T>>;

type UseControllableStateParams<T> = {
  prop?: T | undefined;
  defaultProp: T;
  onChange?: ChangeHandler<T>;
};

export function useControllableState<T>({
  prop,
  defaultProp,
  onChange = () => {
    // no-op default so callers can omit onChange
  },
}: UseControllableStateParams<T>): [T, SetStateFn<T>] {
  const [uncontrolledProp, setUncontrolledProp] = useUncontrolledState({
    defaultProp,
    onChange,
  });
  const isControlled = prop !== undefined;
  const value = (isControlled ? prop : uncontrolledProp) as T;
  const handleChange = useCallbackRef(onChange);

  const setValue: SetStateFn<T> = useCallback(
    (nextValue) => {
      if (isControlled) {
        const setter = nextValue as (prev: T) => T;
        const next =
          typeof nextValue === "function" ? setter(prop) : (nextValue as T);
        if (next !== prop) {
          handleChange(next);
        }
      } else {
        setUncontrolledProp(nextValue as SetStateAction<T | undefined>);
      }
    },
    [isControlled, prop, setUncontrolledProp, handleChange],
  );

  return [value, setValue];
}

type UseUncontrolledStateParams<T> = {
  defaultProp: T;
  onChange?: ChangeHandler<T>;
};

function useUncontrolledState<T>({
  defaultProp,
  onChange,
}: UseUncontrolledStateParams<T>) {
  const uncontrolledState = useState<T | undefined>(defaultProp);
  const [value] = uncontrolledState;
  const prevValueRef = useRef(value);
  const handleChange = useCallbackRef(onChange);

  useEffect(() => {
    if (prevValueRef.current !== value) {
      handleChange(value as T);
      prevValueRef.current = value;
    }
  }, [value, handleChange]);

  return uncontrolledState;
}

// Stable callback ref so the returned setValue identity doesn't change
// when callers pass a new onChange every render. useInsertionEffect
// (not useEffect / useLayoutEffect) is intentional: it fires *before*
// any descendant layout effect in any subtree, so a child layout
// effect that calls setValue in the same commit as the parent supplying
// a new onChange always reads the latest callback. useEffect would
// leave the ref stale until after paint.
function useCallbackRef<T extends (...args: never[]) => unknown>(
  callback: T | undefined,
): T {
  const callbackRef = useRef(callback);
  useInsertionEffect(() => {
    callbackRef.current = callback;
  });
  // biome-ignore lint/correctness/useExhaustiveDependencies: ref pattern intentionally has empty deps so the returned function identity is stable
  return useMemo(
    () => ((...args: never[]) => callbackRef.current?.(...args)) as T,
    [],
  );
}
